import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search, MapPin, Clock, Euro, Calendar, Building2, Users,
  Briefcase, Filter, ChevronDown, Monitor, Send, Eye, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { ApplicationModal } from '@/components/missions/ApplicationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work mode labels
const WORK_MODE_LABELS = {
  onsite: 'Sur site',
  remote: 'Télétravail',
  hybrid: 'Hybride',
};

// Duration labels
const DURATION_LABELS = {
  '1_week': '1 semaine',
  '2_weeks': '2 semaines',
  '1_month': '1 mois',
  '2_months': '2 mois',
  '3_months': '3 mois',
  '6_months': '6 mois',
  '1_year': '1 an',
  'ongoing': 'Durée indéterminée',
};

// Mission Card Component
const MissionCard = ({ mission, onApply, onViewDetails }) => {
  const isLoggedIn = !!localStorage.getItem('sfretire_token');
  const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
  const isRetraite = user?.role === 'retraite' || user?.user_type === 'retraite';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground line-clamp-2">
            {mission.title}
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
              <Briefcase size={12} />
              {mission.sector || 'Multi-secteur'}
            </span>
            {mission.work_mode && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                <Monitor size={12} />
                {WORK_MODE_LABELS[mission.work_mode] || mission.work_mode}
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          {mission.budget_max && (
            <p className="text-lg font-bold text-primary">
              {mission.budget_min && `${mission.budget_min} - `}{mission.budget_max}€
              <span className="text-sm font-normal text-muted-foreground">
                /{mission.rate_type === 'hourly' ? 'h' : mission.rate_type === 'daily' ? 'j' : ''}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
        {mission.description}
      </p>

      {/* Skills */}
      {mission.required_skills && mission.required_skills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {mission.required_skills.slice(0, 5).map((skill, idx) => (
            <span
              key={idx}
              className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-md"
            >
              {skill}
            </span>
          ))}
          {mission.required_skills.length > 5 && (
            <span className="px-2 py-1 text-xs text-muted-foreground">
              +{mission.required_skills.length - 5} autres
            </span>
          )}
        </div>
      )}

      {/* Meta info */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-4">
        {mission.location && (
          <span className="flex items-center gap-1">
            <MapPin size={14} />
            {mission.location}
          </span>
        )}
        {mission.duration && (
          <span className="flex items-center gap-1">
            <Clock size={14} />
            {DURATION_LABELS[mission.duration] || mission.duration}
          </span>
        )}
        {mission.experience_required && (
          <span className="flex items-center gap-1">
            <Users size={14} />
            {mission.experience_required}+ ans exp.
          </span>
        )}
        {mission.start_date && (
          <span className="flex items-center gap-1">
            <Calendar size={14} />
            Début: {new Date(mission.start_date).toLocaleDateString('fr-FR')}
          </span>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground border-t border-border pt-4 mb-4">
        <span className="flex items-center gap-1">
          <Eye size={12} />
          {mission.views_count || 0} vues
        </span>
        <span className="flex items-center gap-1">
          <Users size={12} />
          {mission.applications_count || 0} candidatures
        </span>
        <span className="flex items-center gap-1">
          <Calendar size={12} />
          Publié le {new Date(mission.published_at || mission.created_at).toLocaleDateString('fr-FR')}
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => onViewDetails(mission)}
        >
          Voir les détails
        </Button>
        {isLoggedIn && isRetraite && (
          <Button
            variant="gold"
            className="flex-1"
            onClick={() => onApply(mission)}
          >
            <Send size={16} className="mr-2" />
            Postuler
          </Button>
        )}
        {!isLoggedIn && (
          <Button
            variant="gold"
            className="flex-1"
            onClick={() => {
              toast.info('Connectez-vous pour postuler');
              window.location.href = '/retraites';
            }}
          >
            <Send size={16} className="mr-2" />
            Postuler
          </Button>
        )}
      </div>
    </motion.div>
  );
};

// Mission Detail Modal
const MissionDetailModal = ({ mission, isOpen, onClose, onApply }) => {
  if (!isOpen || !mission) return null;

  const isLoggedIn = !!localStorage.getItem('sfretire_token');
  const user = JSON.parse(localStorage.getItem('sfretire_user') || 'null');
  const isRetraite = user?.role === 'retraite' || user?.user_type === 'retraite';

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl shadow-elevated w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-serif font-bold text-foreground">
                {mission.title}
              </h2>
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {mission.sector || 'Multi-secteur'}
                </span>
                {mission.work_mode && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary">
                    {WORK_MODE_LABELS[mission.work_mode]}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
              ×
            </button>
          </div>

          {/* Budget */}
          {mission.budget_max && (
            <div className="bg-primary/5 rounded-xl p-4 mb-6">
              <p className="text-sm text-muted-foreground">Budget</p>
              <p className="text-2xl font-bold text-primary">
                {mission.budget_min && `${mission.budget_min} - `}{mission.budget_max}€
                <span className="text-base font-normal text-muted-foreground">
                  /{mission.rate_type === 'hourly' ? 'heure' : mission.rate_type === 'daily' ? 'jour' : 'forfait'}
                </span>
              </p>
            </div>
          )}

          {/* Description */}
          <div className="mb-6">
            <h3 className="font-semibold text-foreground mb-2">Description</h3>
            <p className="text-muted-foreground whitespace-pre-line">
              {mission.description}
            </p>
          </div>

          {/* Skills */}
          {mission.required_skills && mission.required_skills.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-foreground mb-2">Compétences requises</h3>
              <div className="flex flex-wrap gap-2">
                {mission.required_skills.map((skill, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            {mission.location && (
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <MapPin className="text-muted-foreground" size={18} />
                <div>
                  <p className="text-xs text-muted-foreground">Localisation</p>
                  <p className="font-medium">{mission.location}</p>
                </div>
              </div>
            )}
            {mission.duration && (
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <Clock className="text-muted-foreground" size={18} />
                <div>
                  <p className="text-xs text-muted-foreground">Durée</p>
                  <p className="font-medium">{DURATION_LABELS[mission.duration] || mission.duration}</p>
                </div>
              </div>
            )}
            {mission.experience_required && (
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <Users className="text-muted-foreground" size={18} />
                <div>
                  <p className="text-xs text-muted-foreground">Expérience requise</p>
                  <p className="font-medium">{mission.experience_required}+ années</p>
                </div>
              </div>
            )}
            {mission.start_date && (
              <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
                <Calendar className="text-muted-foreground" size={18} />
                <div>
                  <p className="text-xs text-muted-foreground">Date de début</p>
                  <p className="font-medium">{new Date(mission.start_date).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
            )}
          </div>

          {/* Apply button */}
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Fermer
            </Button>
            {isLoggedIn && isRetraite && (
              <Button variant="gold" onClick={() => onApply(mission)} className="flex-1">
                <Send size={16} className="mr-2" />
                Postuler à cette mission
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Main Missions Page
const MissionsPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [missions, setMissions] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    sector: '',
    work_mode: '',
  });
  const [selectedMission, setSelectedMission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    hasMore: false,
  });

  useEffect(() => {
    fetchMissions();
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sectors`);
      const data = await res.json();
      setSectors(data.sectors || []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
    }
  };

  const fetchMissions = async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '12',
        published_only: 'true',
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.work_mode) params.append('work_mode', filters.work_mode);

      const res = await fetch(`${API_URL}/api/missions?${params}`);
      const data = await res.json();

      setMissions(data.items || []);
      setPagination({
        page: data.page || 1,
        total: data.total || 0,
        hasMore: data.has_more || false,
      });
    } catch (error) {
      console.error('Error fetching missions:', error);
      toast.error('Erreur lors du chargement des missions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMissions(1);
  };

  const handleViewDetails = (mission) => {
    setSelectedMission(mission);
    setShowDetailModal(true);
  };

  const handleApply = (mission) => {
    setSelectedMission(mission);
    setShowDetailModal(false);
    setShowApplyModal(true);
  };

  const handleApplicationSuccess = () => {
    setShowApplyModal(false);
    setSelectedMission(null);
    toast.success('Votre candidature a été envoyée !');
    fetchMissions(pagination.page);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary/5 to-transparent py-12">
          <div className="container mx-auto px-4 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
                Missions disponibles
              </h1>
              <p className="text-lg text-muted-foreground">
                Découvrez les opportunités qui correspondent à votre expertise et postulez en quelques clics.
              </p>
            </motion.div>

            {/* Search & Filters */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              onSubmit={handleSearch}
              className="mt-8 bg-card rounded-xl p-4 shadow-soft"
            >
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
                  <Input
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Rechercher par mot-clé..."
                    className="pl-10"
                  />
                </div>
                
                <select
                  value={filters.sector}
                  onChange={(e) => setFilters(prev => ({ ...prev, sector: e.target.value }))}
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Tous les secteurs</option>
                  {sectors.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                <select
                  value={filters.work_mode}
                  onChange={(e) => setFilters(prev => ({ ...prev, work_mode: e.target.value }))}
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">Tous les modes</option>
                  <option value="onsite">Sur site</option>
                  <option value="remote">Télétravail</option>
                  <option value="hybrid">Hybride</option>
                </select>

                <Button type="submit" variant="gold">
                  <Search size={18} className="mr-2" />
                  Rechercher
                </Button>
              </div>
            </motion.form>
          </div>
        </section>

        {/* Missions List */}
        <section className="py-12">
          <div className="container mx-auto px-4 lg:px-8">
            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-muted-foreground">
                <span className="font-semibold text-foreground">{pagination.total}</span> mission{pagination.total !== 1 ? 's' : ''} trouvée{pagination.total !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            )}

            {/* Empty state */}
            {!loading && missions.length === 0 && (
              <div className="text-center py-16">
                <Briefcase className="mx-auto mb-4 text-muted-foreground opacity-50" size={48} />
                <h3 className="text-lg font-semibold text-foreground">Aucune mission disponible</h3>
                <p className="text-muted-foreground mt-2">
                  Revenez bientôt pour découvrir de nouvelles opportunités
                </p>
              </div>
            )}

            {/* Missions grid */}
            {!loading && missions.length > 0 && (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {missions.map((mission, idx) => (
                    <MissionCard
                      key={mission.id}
                      mission={mission}
                      onViewDetails={handleViewDetails}
                      onApply={handleApply}
                    />
                  ))}
                </div>

                {/* Load more */}
                {pagination.hasMore && (
                  <div className="text-center mt-8">
                    <Button
                      variant="outline"
                      onClick={() => fetchMissions(pagination.page + 1)}
                    >
                      Charger plus de missions
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />

      {/* Detail Modal */}
      <MissionDetailModal
        mission={selectedMission}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedMission(null);
        }}
        onApply={handleApply}
      />

      {/* Application Modal */}
      <ApplicationModal
        mission={selectedMission}
        isOpen={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setSelectedMission(null);
        }}
        onSuccess={handleApplicationSuccess}
      />
    </div>
  );
};

export default MissionsPage;
